import React, { useState } from "react";
import "../styles/button.css";
import "../styles/contact.css";
import { CONTACT } from "../config/site";

/**
 * Button — when `trigger` is true, clicking opens a popup contact form.
 * Otherwise it's a plain button that fires `props.onClick` (or follows `props.href`).
 *
 * On submit, POSTs to /api/contact (see api/contact.ts).
 * Shows "Thank You" on success; shows a real error on failure (no more silent swallow).
 */
const Button = (props) => {
  const [trigger, setTrigger] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  // Honeypot — bots will fill `website`; humans never see it
  const [website, setWebsite] = useState("");

  const [isValidEmail, setIsValidEmail] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error
  const [errorMsg, setErrorMsg] = useState("");

  const closePopup = () => {
    setTrigger(false);
    setStatus("idle");
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();

    if (!username.trim() || !email.trim()) {
      setStatus("error");
      setErrorMsg("Please enter your name and email.");
      return;
    }
    if (!isValidEmail) {
      setStatus("error");
      setErrorMsg("Please enter a valid email address.");
      return;
    }

    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(CONTACT.formEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: username,
          email,
          phone,
          message,
          website, // honeypot
          source: props.id || "contact-button",
          page:
            typeof window !== "undefined" ? window.location.pathname : "",
          type: "contact",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setStatus("ok");
        setUsername("");
        setEmail("");
        setPhone("");
        setMessage("");
        setTimeout(closePopup, 2500);
      } else {
        setStatus("error");
        setErrorMsg(
          "Sorry, we couldn't send your message. Please email " +
            CONTACT.email +
            " directly."
        );
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        "Network error. Please email " + CONTACT.email + " directly."
      );
    }
  };

  const handleBtnClick = () => setTrigger((v) => !v);

  const validateEmail = (e) => {
    setEmail(e.target.value);
    setIsValidEmail(e.target.checkValidity());
  };

  if (props.trigger) {
    return (
      <>
        <button
          id={props.id}
          type="button"
          className="btn"
          onClick={handleBtnClick}
        >
          {props.label}
        </button>
        {trigger && (
          <div className="popup">
            <div className="popup-inner">
              <button
                type="button"
                className="close-btn"
                onClick={closePopup}
                aria-label="Close"
              >
                X
              </button>
              {status === "ok" ? (
                <>
                  <h2 className="contact">Thank you!</h2>
                  <h5>We've received your message and will get back shortly.</h5>
                </>
              ) : (
                <form className="contact" onSubmit={handleSubmit} noValidate>
                  <h2>Contact Us</h2>

                  {/* Honeypot — hidden from real users */}
                  <input
                    type="text"
                    name="website"
                    autoComplete="off"
                    tabIndex={-1}
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "-10000px",
                      width: 1,
                      height: 1,
                      opacity: 0,
                    }}
                    aria-hidden="true"
                  />

                  <input
                    type="text"
                    className="field"
                    placeholder="Your Name"
                    onChange={(e) => setUsername(e.target.value)}
                    value={username}
                    required
                  />
                  <input
                    type="email"
                    className="field"
                    placeholder="Your Email"
                    onChange={validateEmail}
                    value={email}
                    pattern="^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$"
                    required
                  />
                  {!isValidEmail && (
                    <span id="error">
                      Must match standard email format xxx@xxx.xxx
                    </span>
                  )}
                  <input
                    type="tel"
                    className="field"
                    placeholder="Your Phone"
                    onChange={(e) => setPhone(e.target.value)}
                    value={phone}
                  />
                  <textarea
                    placeholder="Message"
                    className="field"
                    onChange={(e) => setMessage(e.target.value)}
                    value={message}
                  />
                  {status === "error" && (
                    <p style={{ color: "#c0392b", marginTop: 8 }}>
                      {errorMsg}
                    </p>
                  )}
                  <button
                    type="submit"
                    className="btn"
                    disabled={status === "sending"}
                  >
                    {status === "sending" ? "Sending…" : "Send"}
                  </button>
                  <p
                    style={{
                      fontSize: 11,
                      color: "#666",
                      marginTop: 10,
                    }}
                  >
                    By submitting, you agree to our{" "}
                    <a href="/privacy-policy">Privacy Policy</a>.
                  </p>
                </form>
              )}
            </div>
          </div>
        )}
      </>
    );
  }
  return (
    <button
      id={props.id}
      type="button"
      className="btn"
      onClick={props.onClick}
    >
      {props.label}
    </button>
  );
};

export default Button;
